import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

export type AnnualBenefitFormValues = {
  value: string;
  description: string;
  startDate: Moment | null;
  endDate: Moment | null;
  eeContribution: string;
  totalEeContribution: string;
};

export type AnnualBenefitFormRef = {
  submit: () => void;
  setFieldsValue: (values: Partial<AnnualBenefitFormValues>) => void;
};

type AnnualBenefitFormProps = {
  formRef?: Ref<AnnualBenefitFormRef>;
  onSubmit?: (values: AnnualBenefitFormValues) => void;
  defaultValues?: AnnualBenefitFormValues;
};

const AnnualBenefitForm: React.FC<AnnualBenefitFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<AnnualBenefitFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    setFieldsValue(values) {
      form.setFieldsValue(values);
    },
  }));

  const onFinish = (values: AnnualBenefitFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={defaultValues}
    >
      <Form.Item
        label={messages['common.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.value']}
        name="value"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.startDate']}
        name="startDate"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
      <Form.Item label={messages['common.endDate']} name="endDate">
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.employeeContribution']}
        name="eeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.totalEmployeeContribution']}
        name="totalEeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
          disabled
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default AnnualBenefitForm;
